'use client'

import ProductGuestDialog from '@/app/(layout)/[serviceUrl]/_components/ProductGuestDialog'
import ProductParticipantCounter from '@/app/(layout)/[serviceUrl]/_components/ProductParticipantCounter'
import ProductPaymentMethodFields, {
  IPaymentMethodValues,
} from '@/app/(layout)/[serviceUrl]/_components/ProductPaymentMethodFields'
import {
  formatProductPrice,
  hasProductPrice,
} from '@/app/(layout)/[serviceUrl]/_utils/productPrice'
import { Calendar } from '@/components/ui/calendar'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import { toast } from '@/components/ui/use-toast'
import { useAuthContext } from '@/context/AuthContext'
import { useModals } from '@/hooks/useModals'
import { trackAddToCart } from '@/lib/analytics'
import { formatDateTime, SERVICE_TIMEZONE } from '@/lib/Dayjs'
import { PORTONE_PAY_METHOD } from '@/lib/PortOne/portOne.constants'
import { requestPortOnePay } from '@/lib/PortOne/portOne.sdk'
import {
  clearPortOneRedirectQuery,
  getCurrentPageUrl,
  parsePortOneRedirectResult,
} from '@/lib/PortOne/portOne.utils'
import { paymentApi } from '@/service/payments'
import { IProduct, IProductSchedule } from '@/types/interfaces/products'
import { getErrorMessage } from '@/utils/error'
import { format } from 'date-fns'
import { X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'

export const productPaymentConfirmation = (
  name: string,
  isSchedule: boolean,
  date?: string,
  time?: string
) =>
  `${name ? `${name}님, ` : ''}${date && time ? `${date} ${time} 예약이` : isSchedule ? '예약이' : '구매가'} 완료되었습니다.`

const availableSchedules = (schedules: IProductSchedule[]) => {
  return schedules.filter(schedule => {
    return schedule.state === 'ENABLED' && schedule.capacity > schedule.reservedCount
  })
}

interface IReservationValues extends IPaymentMethodValues {
  selectedDate?: Date
  selectedScheduleId?: number
  adultCount: number
  childCount: number
  infantCount: number
}

const reservationDefaults: IReservationValues = {
  adultCount: 1,
  childCount: 0,
  infantCount: 0,
  payMethod: PORTONE_PAY_METHOD.CARD,
  refundHolder: '',
  refundBankCode: '',
  refundAccount: '',
}

type TMobileStep = 'date' | 'details'

interface IReservationFormProps {
  product: IProduct
  termsPath: string
}

export default function ProductDetailReservationForm({
  product,
  termsPath,
}: IReservationFormProps) {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [mobileStep, setMobileStep] = useState<TMobileStep>('date')
  const [guestOpen, setGuestOpen] = useState(false)
  const { isLogin, userData } = useAuthContext()
  const { alertModal } = useModals()
  const [isPaying, setIsPaying] = useState(false)
  const payingRef = useRef(false)
  const redirectHandledRef = useRef(false)
  const { watch, getValues, setValue, reset, handleSubmit } = useForm<IReservationValues>({
    defaultValues: reservationDefaults,
  })
  const {
    selectedDate,
    selectedScheduleId,
    adultCount,
    childCount,
    infantCount,
    payMethod,
    refundHolder,
    refundBankCode,
    refundAccount,
  } = watch()
  const isSchedule = product.type === 'SCHEDULE'
  const schedulesByDate = useMemo(() => {
    const dates = new Map<string, IProduct['schedules']>()
    availableSchedules(product.schedules ?? []).forEach(schedule => {
      const date = schedule.date.slice(0, 10)
      dates.set(date, [...(dates.get(date) ?? []), schedule])
    })
    dates.forEach(schedules => {
      schedules.sort((a, b) => {
        return a.order - b.order || a.startTime.localeCompare(b.startTime)
      })
    })
    return dates
  }, [product.schedules])
  const today = new Date()
  const lastScheduleDate = Array.from(schedulesByDate.keys()).sort().pop()
  const lastNavigableMonth =
    lastScheduleDate && lastScheduleDate > format(today, 'yyyy-MM-dd')
      ? new Date(`${lastScheduleDate}T00:00:00`)
      : today
  const selectedDateKey = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : ''
  const dateSchedules = schedulesByDate.get(selectedDateKey) ?? []
  const selectedSchedule = dateSchedules.find(schedule => {
    return schedule.id === selectedScheduleId
  })
  const remaining = selectedSchedule
    ? selectedSchedule.capacity - selectedSchedule.reservedCount
    : undefined
  const adultPrice = Number(product.mainPrice.replace(/[^\d.-]/g, '')) || 0
  const childPrice = Number(product.childrenPrice?.replace(/[^\d.-]/g, '')) || 0
  const infantPrice = Number(product.infantsPrice?.replace(/[^\d.-]/g, '')) || 0
  const hasChildPrice = hasProductPrice(product.childrenPrice)
  const hasInfantPrice = hasProductPrice(product.infantsPrice)
  const hasAgeBasedPricing = hasChildPrice || hasInfantPrice
  const totalPrice = adultPrice * adultCount + childPrice * childCount + infantPrice * infantCount

  const completePayment = useCallback(
    async (paymentId: string) => {
      const { data } = await paymentApi.completeProduct(product.userId, product.id, { paymentId })
      const key = `product-payment:${paymentId}`
      const details = sessionStorage.getItem(key)
      sessionStorage.removeItem(key)
      reset(reservationDefaults)
      setMobileStep('date')
      if (data.state === 'VIRTUAL_ACCOUNT_ISSUED') {
        const account = data.virtualAccount
        alertModal({
          title: '가상계좌 발급 완료',
          message: (
            <div className="space-y-4 text-left text-sm leading-6">
              <p>
                가상계좌가 발급되었습니다.
                {account && ' 아래 계좌로 입금 기한 내에 송금해 주세요.'}
              </p>
              {account && (
                <dl className="space-y-2 rounded-lg bg-gray-50 p-4">
                  <div className="flex justify-between gap-4">
                    <dt className="shrink-0 text-gray-500">은행</dt>
                    <dd className="text-right font-semibold">{account.bankName}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="shrink-0 text-gray-500">계좌번호</dt>
                    <dd className="break-all text-right font-semibold">{account.accountNumber}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="shrink-0 text-gray-500">예금주</dt>
                    <dd className="text-right font-semibold">{account.holder}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="shrink-0 text-gray-500">입금 기한</dt>
                    <dd className="text-right font-semibold">
                      {formatDateTime(account.dueDate, 'YYYY년 M월 D일 HH:mm', SERVICE_TIMEZONE)}
                      까지 (한국시간)
                    </dd>
                  </div>
                </dl>
              )}
              <p>입금이 확인되면 {isSchedule ? '예약이' : '구매가'} 확정됩니다.</p>
            </div>
          ),
          rightBtn: { label: '확인' },
        })
        return
      }

      const { name, date, time } = details
        ? (JSON.parse(details) as { name: string; date?: string; time?: string })
        : { name: userData?.name || '', date: undefined, time: undefined }
      alertModal({
        title: isSchedule ? '예약 완료' : '구매 완료',
        message: productPaymentConfirmation(name, isSchedule, date, time),
        rightBtn: { label: '확인' },
      })
    },
    [alertModal, isSchedule, product.id, product.userId, reset, userData?.name]
  )

  useEffect(() => {
    if (redirectHandledRef.current) return
    const result = parsePortOneRedirectResult(window.location.search)
    if (!result) return

    redirectHandledRef.current = true
    clearPortOneRedirectQuery()
    if (result.code != null) {
      toast({ variant: 'error', description: result.message || '결제에 실패했습니다.' })
      return
    }

    payingRef.current = true
    setIsPaying(true)
    completePayment(result.paymentId)
      .catch(error => {
        console.error(error)
        toast({
          variant: 'error',
          description: '결제 확인에 실패했습니다. 다시 결제하기 전에 결제 내역을 확인해주세요.',
        })
      })
      .finally(() => {
        payingRef.current = false
        setIsPaying(false)
      })
  }, [completePayment])

  const preparePayment = async (
    values: IReservationValues,
    buyer: { name: string | null; phoneNumber: string | null; email?: string | null },
    guestId?: number
  ) => {
    if (payingRef.current) return
    if (!buyer.name || !buyer.phoneNumber) {
      toast({ variant: 'error', description: '결제에 필요한 이름과 연락처를 확인해주세요.' })
      return
    }
    payingRef.current = true
    setIsPaying(true)
    try {
      const options = [
        {
          name: hasAgeBasedPricing ? '성인' : '1인',
          quantity: values.adultCount,
          price: adultPrice,
        },
        hasChildPrice && values.childCount > 0
          ? { name: '아동', quantity: values.childCount, price: childPrice }
          : null,
        hasInfantPrice && values.infantCount > 0
          ? { name: '유아', quantity: values.infantCount, price: infantPrice }
          : null,
      ].filter(Boolean) as Array<{ name: string; quantity: number; price: number }>

      trackAddToCart({
        product,
        options,
        totalAmount:
          adultPrice * values.adultCount +
          childPrice * values.childCount +
          infantPrice * values.infantCount,
      })

      const { data: prepared } = await paymentApi.prepareProduct(product.userId, product.id, {
        payMethod: values.payMethod,
        ...(isSchedule && { productScheduleId: values.selectedScheduleId }),
        ...(guestId !== undefined && { guestId }),
        adultCount: values.adultCount,
        childrenCount: values.childCount,
        infantsCount: values.infantCount,
        buyer: {
          name: buyer.name,
          phoneNumber: buyer.phoneNumber,
          ...(buyer.email && { email: buyer.email }),
        },
        ...(values.payMethod === PORTONE_PAY_METHOD.VIRTUAL_ACCOUNT && {
          refund: {
            holder: values.refundHolder.trim(),
            bankCode: values.refundBankCode.trim(),
            account: values.refundAccount.trim(),
          },
        }),
      })
      const schedule = product.schedules.find(item => item.id === values.selectedScheduleId)
      sessionStorage.setItem(
        `product-payment:${prepared.paymentId}`,
        JSON.stringify({
          name: buyer.name,
          date: values.selectedDate ? format(values.selectedDate, 'M월 d일') : undefined,
          time: schedule?.startTime.slice(0, 5),
        })
      )
      setMobileOpen(false)
      const result = await requestPortOnePay({
        prepared,
        payMethod: values.payMethod,
        buyer: {
          name: buyer.name,
          phoneNumber: buyer.phoneNumber,
          ...(buyer.email && { email: buyer.email }),
        },
        redirectUrl: getCurrentPageUrl(),
      })
      if (!result) return
      if (result.code != null) {
        toast({ variant: 'error', description: result.message || '결제에 실패했습니다.' })
        return
      }
      try {
        await completePayment(result.paymentId)
      } catch (error) {
        console.error(error)
        toast({
          variant: 'error',
          description: '결제 확인에 실패했습니다. 다시 결제하기 전에 결제 내역을 확인해주세요.',
        })
      }
    } catch (error) {
      toast({ variant: 'error', description: getErrorMessage(error, '결제 처리에 실패했습니다.') })
    } finally {
      payingRef.current = false
      setIsPaying(false)
    }
  }

  const handleReserve = (values: IReservationValues) => {
    if (product.state !== 'AVAILABLE') {
      return
    }
    const schedule = schedulesByDate
      .get(values.selectedDate ? format(values.selectedDate, 'yyyy-MM-dd') : '')
      ?.find(item => {
        return item.id === values.selectedScheduleId
      })

    if (
      isSchedule &&
      (!schedule ||
        values.adultCount + values.childCount + values.infantCount >
          schedule.capacity - schedule.reservedCount)
    ) {
      return
    }

    if (
      values.payMethod === PORTONE_PAY_METHOD.VIRTUAL_ACCOUNT &&
      (!values.refundHolder.trim() || !values.refundBankCode.trim() || !values.refundAccount.trim())
    ) {
      toast({ variant: 'error', description: '환불 계좌 정보를 모두 입력해주세요.' })
      return
    }

    if (!isLogin) {
      if (product.isMemberOnly) {
        setMobileOpen(false)
        alertModal({
          title: `회원만 ${isSchedule ? '예약' : '구매'}할 수 있는 상품입니다.`,
          rightBtn: { label: '확인' },
        })
        return
      }
      setMobileOpen(false)
      setGuestOpen(true)
      return
    }

    if (userData) return preparePayment(values, userData)
  }

  const participantSummary = [
    `${hasAgeBasedPricing ? '성인' : '1인'} ${adultCount}명`,
    hasChildPrice && childCount > 0 ? `아동 ${childCount}명` : null,
    hasInfantPrice && infantCount > 0 ? `유아 ${infantCount}명` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  const calendarField = isSchedule && (
    <div className="mt-7">
      <Calendar
        mode="single"
        showDayLabels
        fromMonth={today}
        toMonth={lastNavigableMonth}
        defaultMonth={selectedDate ?? today}
        selected={selectedDate}
        onSelect={date => {
          reset({
            ...getValues(),
            selectedDate: date,
            selectedScheduleId: undefined,
            adultCount: 1,
            childCount: 0,
            infantCount: 0,
          })
          if (date) {
            setMobileStep('details')
          }
        }}
        disabled={date => {
          return (
            date < new Date(new Date().setHours(0, 0, 0, 0)) ||
            !schedulesByDate.has(format(date, 'yyyy-MM-dd'))
          )
        }}
        showOutsideDays={false}
        className="p-0"
        formatters={{
          formatCaption: date => {
            return `${date.getFullYear()}년 ${date.getMonth() + 1}월`
          },
        }}
        classNames={{
          month: 'w-full space-y-4',
          caption_label: 'text-sm font-bold',
          head_row: 'flex w-full',
          head_cell: 'text-muted-foreground flex-1 font-normal text-[0.8rem]',
          row: 'flex w-full mt-2',
          cell: 'relative min-w-0 p-0 text-center text-sm focus-within:relative focus-within:z-20 flex-1 aspect-square [&:has([aria-selected])]:rounded-[10px]',
          day_selected:
            '!rounded-[6px] bg-gray-900 text-white hover:bg-gray-800 hover:text-white focus:bg-gray-900 focus:text-white',
          day_today: 'font-bold text-emerald-600',
          day_disabled: 'text-gray-200 cursor-not-allowed',
        }}
      />
      {!selectedDate && (
        <p className="mt-3 text-xs text-gray-400">체험을 원하는 날짜를 선택해 주세요.</p>
      )}
    </div>
  )

  const timeField = isSchedule && selectedDate && (
    <div className="mt-6">
      <p className="mb-3 text-sm font-bold">시간 선택</p>
      <div className="grid grid-cols-2 gap-2">
        {dateSchedules.map(schedule => {
          return (
            <button
              key={schedule.id}
              type="button"
              aria-pressed={selectedScheduleId === schedule.id}
              onClick={() => {
                reset({
                  ...getValues(),
                  selectedDate,
                  selectedScheduleId: schedule.id,
                  adultCount: 1,
                  childCount: 0,
                  infantCount: 0,
                })
              }}
              className={`rounded-xl border p-3 text-left text-sm ${selectedScheduleId === schedule.id ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 hover:border-gray-900'}`}
            >
              <span className="block font-semibold">{schedule.startTime.slice(0, 5)}</span>
              <span
                className={`mt-1 block text-xs ${selectedScheduleId === schedule.id ? 'text-gray-300' : 'text-gray-400'}`}
              >
                잔여 {schedule.capacity - schedule.reservedCount}명
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )

  const peopleField = (!isSchedule || selectedSchedule) && (
    <div className="mt-5 space-y-5 lg:mt-8">
      <ProductParticipantCounter
        label={hasAgeBasedPricing ? '성인' : '1인'}
        price={product.mainPrice}
        value={adultCount}
        min={1}
        max={remaining == null ? undefined : remaining - childCount - infantCount}
        onChange={value => {
          setValue('adultCount', value)
        }}
      />
      {hasChildPrice && (
        <ProductParticipantCounter
          label="아동"
          price={product.childrenPrice}
          value={childCount}
          min={0}
          max={remaining == null ? undefined : remaining - adultCount - infantCount}
          onChange={value => {
            setValue('childCount', value)
          }}
        />
      )}
      {hasInfantPrice && (
        <ProductParticipantCounter
          label="유아"
          price={product.infantsPrice}
          value={infantCount}
          min={0}
          max={remaining == null ? undefined : remaining - adultCount - childCount}
          onChange={value => {
            setValue('infantCount', value)
          }}
        />
      )}
    </div>
  )

  const totalField = (!isSchedule || selectedSchedule) && (
    <div className="mt-7 border-t border-gray-100 pt-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="flex items-center gap-1 text-sm font-bold text-gray-900">이용 금액</p>
          <p className="mt-1 text-xs text-gray-400">{participantSummary} 기준</p>
        </div>
        <strong className="shrink-0 text-lg">{totalPrice.toLocaleString('ko-KR')}원</strong>
      </div>
    </div>
  )
  const paymentField = (!isSchedule || selectedSchedule) && (
    <ProductPaymentMethodFields
      value={{ payMethod, refundHolder, refundBankCode, refundAccount }}
      onPayMethodChange={method => setValue('payMethod', method)}
      onRefundChange={(field, value) => setValue(field, value)}
    />
  )
  const reserveDisabled =
    product.state !== 'AVAILABLE' || (isSchedule && !selectedSchedule) || isPaying

  return (
    <>
      <form
        onSubmit={handleSubmit(handleReserve)}
        className="scrollbar-thin sticky top-[calc(var(--detail-top,0px)+24px)] hidden max-h-[calc(100dvh-var(--detail-top,0px)-48px)] overflow-y-auto rounded-3xl border border-gray-200 bg-white p-6 shadow-lg lg:!block"
      >
        <div className="flex items-end gap-2">
          <strong className="text-3xl">{formatProductPrice(product.mainPrice, '원')}</strong>
          <span className="pb-1 text-xs text-gray-400">
            / {hasAgeBasedPricing ? '성인 1인 기준' : '1인'}
          </span>
        </div>
        {calendarField}
        {timeField}
        {peopleField}
        {totalField}
        {paymentField}
        <button
          type="submit"
          disabled={reserveDisabled}
          className="mt-5 w-full rounded-xl bg-gray-900 py-4 text-sm font-bold text-white disabled:cursor-not-allowed disabled:bg-gray-300"
        >
          {product.state !== 'AVAILABLE'
            ? '예약 불가'
            : isSchedule && !selectedSchedule
              ? '날짜와 시간을 선택해 주세요'
              : isPaying
                ? '결제 진행 중...'
                : '예약하기'}
        </button>
      </form>

      <div className="fixed inset-x-0 bottom-0 z-[51] flex items-center gap-4 border-t border-gray-100 bg-white px-4 pb-[calc(env(safe-area-inset-bottom)+12px)] pt-3 shadow-[0_-4px_20px_rgba(0,0,0,0.06)] lg:!hidden">
        <div className="min-w-0 flex-1">
          <strong className="block text-lg leading-tight">
            {formatProductPrice(product.mainPrice, '원')}
          </strong>
          <span className="text-xs text-gray-400">
            {hasAgeBasedPricing ? '성인 1인 기준' : '1인 기준'}
          </span>
        </div>
        <button
          type="button"
          aria-label="예약 옵션 열기"
          disabled={product.state !== 'AVAILABLE'}
          onClick={() => {
            setMobileStep(isSchedule && !selectedDate ? 'date' : 'details')
            setMobileOpen(true)
          }}
          className="min-w-[140px] rounded-xl bg-gray-900 px-6 py-3.5 text-sm font-bold text-white disabled:bg-gray-300"
        >
          {product.state === 'AVAILABLE' ? '예약하기' : '예약 불가'}
        </button>
      </div>

      <Drawer open={mobileOpen} onOpenChange={setMobileOpen} shouldScaleBackground={false}>
        <DrawerContent className="z-[60] max-h-dvh min-h-[min(857px,100dvh)] rounded-t-3xl border-gray-200 bg-white lg:hidden [&>div:first-child]:hidden">
          <form onSubmit={handleSubmit(handleReserve)} className="flex min-h-0 flex-1 flex-col">
            <DrawerHeader className="relative px-5 pb-3 pt-5 text-left">
              <DrawerTitle>
                {mobileStep === 'date'
                  ? '날짜 선택'
                  : isSchedule
                    ? '시간 · 인원 선택'
                    : '인원 선택'}
              </DrawerTitle>
              <DrawerDescription className="sr-only">
                날짜를 선택한 뒤 시간과 인원을 선택해 예약하세요.
              </DrawerDescription>
              <DrawerClose asChild>
                <button
                  type="button"
                  aria-label="예약 패널 닫기"
                  className="absolute right-5 top-5 rounded-full p-1 text-gray-500"
                >
                  <X size={20} />
                </button>
              </DrawerClose>
            </DrawerHeader>
            {isSchedule && (
              <div className="grid grid-cols-2 border-b border-gray-100 px-5">
                <button
                  type="button"
                  onClick={() => {
                    setMobileStep('date')
                  }}
                  aria-current={mobileStep === 'date' ? 'step' : undefined}
                  className={`relative flex min-h-[68px] flex-col items-center justify-center gap-1 px-2 text-sm font-semibold ${mobileStep === 'date' ? 'text-gray-900 after:absolute after:inset-x-4 after:bottom-0 after:h-0.5 after:bg-gray-900' : 'text-gray-400'}`}
                >
                  <span>날짜</span>
                  <span className="max-w-full truncate text-xs font-normal text-gray-500">
                    {selectedDate ? format(selectedDate, 'M월 d일') : '날짜를 선택해 주세요'}
                  </span>
                </button>
                <button
                  type="button"
                  disabled={!selectedDate}
                  onClick={() => {
                    setMobileStep('details')
                  }}
                  aria-current={mobileStep === 'details' ? 'step' : undefined}
                  className={`relative flex min-h-[68px] flex-col items-center justify-center gap-1 px-2 text-sm font-semibold disabled:opacity-40 ${mobileStep === 'details' ? 'text-gray-900 after:absolute after:inset-x-4 after:bottom-0 after:h-0.5 after:bg-gray-900' : 'text-gray-400'}`}
                >
                  <span>시간 · 인원</span>
                  <span className="max-w-full truncate text-xs font-normal text-gray-500">
                    {selectedSchedule
                      ? `${selectedSchedule.startTime.slice(0, 5)} · ${participantSummary}`
                      : '시간과 인원을 선택해 주세요'}
                  </span>
                </button>
              </div>
            )}
            <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5">
              {mobileStep === 'date' && calendarField}
              {mobileStep === 'details' && (
                <>
                  {timeField}
                  {(!isSchedule || selectedSchedule) && (
                    <div className={isSchedule ? 'mt-7 border-t border-gray-100 pt-6' : 'mt-7'}>
                      <h3 className="text-sm font-bold">인원 선택</h3>
                    </div>
                  )}
                  {peopleField}
                  {paymentField}
                  {(!isSchedule || selectedSchedule) && (
                    <p className="mt-5 text-xs text-gray-400">{participantSummary} 기준</p>
                  )}
                </>
              )}
            </div>
            <DrawerFooter className="gap-2 border-t border-gray-100 px-5 pb-[calc(env(safe-area-inset-bottom)+16px)] pt-4">
              {mobileStep === 'details' && (!isSchedule || selectedSchedule) && (
                <div className="flex items-center justify-between pb-1 text-sm">
                  <span className="text-gray-500">총 이용 금액</span>
                  <strong className="text-lg">{totalPrice.toLocaleString('ko-KR')}원</strong>
                </div>
              )}
              {mobileStep === 'date' ? (
                <button
                  type="button"
                  disabled={!selectedDate}
                  onClick={() => {
                    setMobileStep('details')
                  }}
                  className="w-full rounded-xl bg-gray-900 py-4 text-sm font-bold text-white disabled:bg-gray-300"
                >
                  시간 · 인원 선택
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={reserveDisabled}
                  className="w-full rounded-xl bg-gray-900 py-4 text-sm font-bold text-white disabled:bg-gray-300"
                >
                  {isPaying
                    ? '결제 진행 중...'
                    : reserveDisabled
                      ? '시간을 선택해 주세요'
                      : '예약하기'}
                </button>
              )}
            </DrawerFooter>
          </form>
        </DrawerContent>
      </Drawer>

      <ProductGuestDialog
        termsPath={termsPath}
        open={guestOpen}
        companyId={product.userId}
        onOpenChange={setGuestOpen}
        onCreated={guest => {
          void preparePayment(getValues(), guest, guest.id)
        }}
      />
    </>
  )
}
